import { Link } from "react-router-dom";
import { useActor, useMachine } from "@xstate/react";
import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./OrderOutfit.module.scss";
import { salePointsMachine } from "../../../xstate/salePoints.machine";
import { AuthState } from "../../../xstate/auth.machine";
import { userCardMachine } from "../../../xstate/userCard.machine";
import apiMethods from "../../../api/apiMethods";
import { Pagination } from "../../Pagination/Pagination";

// Опции для select
const orderPointOptions = [
	{ value: "all", name: "Все" },
	{ value: "new", name: "На проверке дистрибьютором" },
	{
		value: "approved_by_distributor",
		name: "Подтвержденные дистрибьютором: ожидается финализация и начисление баллов",
	},
	{ value: "approved_by_admin", name: "Подтвержденные: баллы начислены" },

	{ value: "rejected_duplicate", name: "Отклоненные: дубликат" },
	{
		value: "rejected_incorrect_data",
		name: "Отклоненные: некорректные данные",
	},
];

// Маппинг статусов для отображения
const statusMapping = {
	approved: "Подтверждено",
	rejected: "Отклонено",
	partially_approved: "Частично подтверждено",
	new: "Ожидает подтверждения",
};

// Доступные поля для сортировки
const sortFields = {
	date: "creation_date",
	pointName: "sale_point",
	uploadDate: "created_at",
};

export function OrderOutfit() {
	const [stateMySalePoints, sendMySalePoints] = useMachine(salePointsMachine);
	const [selectedStatus, setSelectedStatus] = useState("all");
	const [sortConfig, setSortConfig] = useState({
		key: "created_at",
		direction: "desc",
	});
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		perPage: 10,
		total: 0,
		pages: 0,
	});

	let [stateAuth, sendAuth] = useActor(AuthState);
	const [stateUserCard, sendUserCard] = useMachine(userCardMachine);

	const [isSelectOpen, setIsSelectOpen] = useState(false);
	const selectRef = useRef(null);

	// Безопасное получение строки
	const getSafeString = (value, defaultValue = "") => {
		if (!value) return defaultValue;
		if (typeof value === "string") return value;
		if (typeof value === "number") return String(value);
		if (typeof value === "object") {
			if (value.name) return value.name;
			if (value.address) return value.address;
			for (let key in value) {
				if (typeof value[key] === "string" && value[key]) {
					return value[key];
				}
			}
			return defaultValue;
		}
		return defaultValue;
	};

	// Получение статуса из объекта
	const getStatusFromObject = status => {
		if (!status) return "new";
		if (typeof status === "object" && status.slug) return status.slug;
		if (typeof status === "string") return status;
		return "new";
	};

	// Отображение статуса
	const getDisplayStatus = status => {
		if (!status) return "Неизвестно";
		if (typeof status === "object") {
			const slug = status.slug;
			if (slug && statusMapping[slug]) return statusMapping[slug];
			if (status.i18n) {
				if (typeof status.i18n === "string") return status.i18n;
				if (typeof status.i18n === "object")
					return status.i18n.ru || status.i18n.en || "Неизвестно";
			}
			if (status.name) return status.name;
		}
		if (typeof status === "string") return statusMapping[status] || status;
		return "Неизвестно";
	};

	// Получение информации о торговой точке
	const getPointInfo = order => {
		if (order.sale_point) {
			const name = getSafeString(order.sale_point.name);
			const address = getSafeString(order.sale_point.address);

			if (name || address) {
				return (
					<div className={styles.pointInfo}>
						{name && <div className={styles.pointName}>{name}</div>}
						{address && <div className={styles.pointAddress}>{address}</div>}
					</div>
				);
			}
		}
		return (
			getSafeString(order.sale_point_name) ||
			getSafeString(order.point_name) ||
			"Не указано"
		);
	};

	// Функция для загрузки данных с сервера
	const fetchOrders = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			const requestData = {
				perPage: pagination.perPage,
				currentPage: pagination.currentPage,
			};

			// Добавляем статус фильтрации (если не "all")
			if (selectedStatus !== "all") {
				requestData.status = selectedStatus;
			}

			// Добавляем сортировку
			if (sortConfig.key) {
				requestData.orderBy = {
					field: sortConfig.key,
					direction: sortConfig.direction,
				};
			}

			console.log("Request data:", requestData);

			const response =
				await apiMethods.myOrdersList.getDataDistributorList(requestData);

			console.log("API Response:", response);

			// Проверяем структуру ответа
			let ordersData = [];
			let meta = {};

			if (response.data) {
				if (response.data.data) {
					ordersData = response.data.data;
					meta = response.data.meta || {};
				} else if (Array.isArray(response.data)) {
					ordersData = response.data;
				} else {
					ordersData = response.data;
				}
			} else if (Array.isArray(response)) {
				ordersData = response;
			}

			// Форматируем данные
			const formattedOrders = ordersData.map(order => ({
				id: order.id,
				number: order.number || `ZIC-${order.id}`,
				date: formatDate(order.created_at || order.creation_date),
				pointInfo: getPointInfo(order),
				uploadDate: formatDate(order.created_at),
				status: getDisplayStatus(order.status),
				rawStatus: getStatusFromObject(order.status),
			}));

			setOrders(formattedOrders);

			// Обновляем пагинацию из ответа
			if (meta.total || meta.pages) {
				setPagination(prev => ({
					...prev,
					total: meta.total || formattedOrders.length,
					pages:
						meta.pages ||
						Math.ceil((meta.total || formattedOrders.length) / prev.perPage),
					currentPage: meta.current_page || prev.currentPage,
				}));
			} else {
				// Если метаданных нет, вычисляем сами
				const total = formattedOrders.length;
				setPagination(prev => ({
					...prev,
					total: total,
					pages: Math.ceil(total / prev.perPage),
				}));
			}
		} catch (err) {
			console.error("Error fetching orders:", err);
			setError("Не удалось загрузить заказ-наряды");
			setOrders([]);
		} finally {
			setLoading(false);
		}
	}, [selectedStatus, sortConfig, pagination.currentPage, pagination.perPage]);

	// Загружаем данные при изменении фильтров, сортировки или пагинации
	useEffect(() => {
		fetchOrders();
	}, [fetchOrders]);

	// Обработчик смены статуса
	const handleStatusChange = status => {
		setSelectedStatus(status);
		setPagination(prev => ({ ...prev, currentPage: 1 })); // Сбрасываем на первую страницу
	};

	// Обработчик сортировки
	const handleSort = key => {
		const field = sortFields[key];
		if (!field) return;

		setSortConfig(prev => ({
			key: field,
			direction:
				prev.key === field && prev.direction === "asc" ? "desc" : "asc",
		}));
		setPagination(prev => ({ ...prev, currentPage: 1 })); // Сбрасываем на первую страницу
	};

	// Обработчики пагинации
	const handlePrevPage = () => {
		if (pagination.currentPage > 1) {
			setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
		}
	};

	const handleNextPage = () => {
		if (pagination.currentPage < pagination.pages) {
			setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
		}
	};

	useEffect(() => {
		console.log("remove");
		if (stateAuth.context?.has_not_verified_service_orders) {
			const formData = new FormData();
			formData.append("has_not_verified_service_orders", false);
			try {
				sendUserCard({
					type: "updateUser",
					data: formData,
				});
			} catch (e) {
				console.log(e);
			}
		}
	}, [stateAuth.context?.has_not_verified_service_orders]);

	useEffect(() => {
		const handleClickOutside = event => {
			if (selectRef.current && !selectRef.current.contains(event.target)) {
				setIsSelectOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		document.title = "Заказ-наряды | Программа лояльности SK ZIC";
	}, []);

	const formatDate = dateString => {
		if (!dateString) return "";
		try {
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return String(dateString);
			return date.toLocaleString("ru-RU", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			});
		} catch (e) {
			return String(dateString);
		}
	};

	const getStatusContent = (status, orderId, rawStatus) => {
		const statusForAction = rawStatus || status;

		switch (statusForAction) {
			case "approved":
				return (
					<>
						<span className={`${styles.stateValue} ${styles.statusConfirmed}`}>
							{status}
						</span>
						<img src="/images/status_ok.svg" alt="Подтверждено" />
					</>
				);
			case "rejected":
				return (
					<>
						<span className={`${styles.stateValue} ${styles.statusRejected}`}>
							{status}
						</span>
						<img src="/images/status_error.svg" alt="Отклонено" />
					</>
				);
			case "partially_approved":
				return (
					<>
						<span className={`${styles.stateValue} ${styles.statusPartial}`}>
							{status}
						</span>
						<img src="/images/status_wait_2.svg" alt="Частично подтверждено" />
					</>
				);
			case "new":
				return (
					<Link
						to={`/account/order/${orderId}`}
						className={"btn btnPrimary btnIcon newBtnUpdate"}
					>
						Проверить
					</Link>
				);
			default:
				return <span>{status}</span>;
		}
	};

	const getSortIcon = key => {
		const currentField = sortConfig.key;
		const isActive = currentField === sortFields[key];

		if (!isActive) {
			return (
				<svg
					width="12"
					height="7"
					viewBox="0 0 12 7"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M0.625 0.625L5.625 5.625L10.625 0.625001"
						stroke="#4F4D58"
						strokeWidth="1.25"
						strokeLinecap="round"
					/>
				</svg>
			);
		}

		return (
			<svg
				width="12"
				height="7"
				viewBox="0 0 12 7"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				style={{
					transform:
						sortConfig.direction === "desc" ? "rotate(180deg)" : "none",
				}}
			>
				<path
					d="M0.625 0.625L5.625 5.625L10.625 0.625001"
					stroke="#4F4D58"
					strokeWidth="1.25"
					strokeLinecap="round"
				/>
			</svg>
		);
	};

	const selectedOption = orderPointOptions.find(
		opt => opt.value === selectedStatus,
	);

	if (loading && orders.length === 0) {
		return <div className={styles.container}>Загрузка...</div>;
	}

	if (error) {
		return <div className={styles.container}>{error}</div>;
	}

	return (
		<section className={styles.container}>
			<div className="">
				<div className={styles.header}>
					<h1>Заказ-наряды</h1>
				</div>

				<div className={styles.customSelect} ref={selectRef}>
					<div
						className={`${styles.selectTrigger} ${isSelectOpen ? styles.open : ""}`}
						onClick={() => setIsSelectOpen(!isSelectOpen)}
					>
						<span>{selectedOption?.name || "Выберите статус"}</span>
						<svg
							width="12"
							height="7"
							viewBox="0 0 12 7"
							fill="none"
							className={isSelectOpen ? styles.rotated : ""}
						>
							<path
								d="M0.625 0.625L5.625 5.625L10.625 0.625001"
								stroke="#4F4D58"
								strokeWidth="1.25"
								strokeLinecap="round"
							/>
						</svg>
					</div>

					{isSelectOpen && (
						<div className={styles.selectDropdown}>
							{orderPointOptions.map(option => (
								<div
									key={option.value}
									className={`${styles.selectOption} ${selectedStatus === option.value ? styles.selected : ""}`}
									onClick={() => {
										handleStatusChange(option.value);
										setIsSelectOpen(false);
									}}
								>
									{option.name}
								</div>
							))}
						</div>
					)}
				</div>

				<div className={styles.table_div + " scrollbox"}>
					<table className="statTable">
						<thead>
							<tr>
								<th
									onClick={() => handleSort("number")}
									style={{ cursor: "pointer" }}
								>
									<div>Номер и дата заказ-наряда {getSortIcon("number")}</div>
								</th>
								<th
									onClick={() => handleSort("pointName")}
									style={{ cursor: "pointer" }}
								>
									<div>Торговая точка {getSortIcon("pointName")}</div>
								</th>
								<th
									onClick={() => handleSort("uploadDate")}
									style={{ cursor: "pointer" }}
								>
									<div>
										Дата загрузки заказ-наряда {getSortIcon("uploadDate")}
									</div>
								</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{orders.length > 0 ? (
								orders.map(order => {
									return (
										<tr key={order.id}>
											<td>
												<div>
													<a href={`/account/order/` + order.id}>
														{String(order.number)}
													</a>
												</div>
												<div>{String(order.date)}</div>
											</td>
											<td>
												{typeof order.pointInfo === "string"
													? order.pointInfo
													: order.pointInfo}
											</td>
											<td>{String(order.uploadDate)}</td>
											<td className={styles.status}>
												<div>
													{getStatusContent(
														order.status,
														order.id,
														order.rawStatus,
													)}
												</div>
											</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td
										colSpan="4"
										style={{ textAlign: "center", padding: "40px" }}
									>
										{loading ? "Загрузка..." : "Нет данных для отображения"}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{pagination.pages > 1 && (
					<Pagination
						pages={pagination.pages}
						current_page={pagination.currentPage}
						send={action => {
							if (action === "prevPage") {
								handlePrevPage();
							} else if (action === "nextPage") {
								handleNextPage();
							}
						}}
					/>
				)}
			</div>
		</section>
	);
}
